import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchLatestPortalPublications,
  fetchLatestPortalPublicationsByBien,
  invokePortalPublish,
  isPortalInFlight,
  type PortalAction,
  type PortalPublicationRow,
} from "@/lib/portalPublish";
import { IS_DEMO_MODE, type PortalId } from "@/lib/portals";
import { readDemoLatestByBien, readDemoPubs, simulatePortalAction } from "@/lib/portalDemoStore";

export const portalKeys = {
  /** Préfixe commun — à invalider après toute action portail. */
  root: ["portal"] as const,
  all: (bienId: string) => ["portal", "publications", bienId] as const,
  latestByBien: (portal: PortalId) => ["portal", "latest-by-bien", portal] as const,
};

/**
 * Lit toutes les dernières publications d'un bien (par portail).
 *
 * Auto-refetch toutes les 30s tant qu'au moins UN portail n'est pas en
 * état final (PROCESSED/REJECTED/DEACTIVATED/DELETED).
 */
export function usePortalPublications(bienId: string | undefined) {
  return useQuery<Record<PortalId, PortalPublicationRow | null>>({
    queryKey: bienId ? portalKeys.all(bienId) : ["portal", "none"],
    queryFn: () => {
      if (!bienId) return Promise.resolve({ website: null, immoweb: null, spotto: null, zimmo: null });
      // Mode démo : lit depuis localStorage au lieu de Supabase. Évite les
      // 404 silencieux si la migration portal_publications n'est pas appliquée.
      if (IS_DEMO_MODE) return Promise.resolve(readDemoPubs(bienId));
      return fetchLatestPortalPublications(bienId);
    },
    enabled: !!bienId,
    refetchInterval: (q) => {
      const data = q.state.data;
      if (!data) return false;
      const hasInFlight = (Object.values(data) as (PortalPublicationRow | null)[]).some((row) =>
        isPortalInFlight(row),
      );
      return hasInFlight ? 30_000 : false;
    },
  });
}

/**
 * Dernière publication par bien pour un portail — pour une colonne de liste
 * (Publication Vente / Location). Même règle de rafraîchissement que
 * usePortalPublications : 30s tant qu'une ligne est en cours.
 */
export function useLatestPortalPublicationsByBien(portal: PortalId) {
  return useQuery<Record<string, PortalPublicationRow>>({
    queryKey: portalKeys.latestByBien(portal),
    queryFn: () =>
      IS_DEMO_MODE
        ? Promise.resolve(readDemoLatestByBien(portal))
        : fetchLatestPortalPublicationsByBien(portal),
    refetchInterval: (q) => {
      const data = q.state.data;
      if (!data) return false;
      return Object.values(data).some((row) => isPortalInFlight(row)) ? 30_000 : false;
    },
  });
}

/**
 * Déclenche une action portail (publish/update/deactivate/delete) puis
 * invalide le cache.
 *
 * En mode démo, l'écriture localStorage est synchrone — on attend explicitement
 * l'invalidation dans onSuccess pour que les consommateurs (matrice, pastilles
 * mini) refetchent avant que le caller (modal) ferme la vue. Pour le cycle
 * multi-étapes (immoweb : PENDING → MEDIA_PROCESSING → PROCESSED via
 * setTimeout), on garde un onTick qui invalide à chaque transition.
 */
export function usePortalPublishMutation(bienId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { portal: PortalId; action: PortalAction; force?: boolean }) => {
      if (IS_DEMO_MODE) {
        simulatePortalAction({
          bienId,
          portal: args.portal,
          action: args.action,
          onTick: () => {
            qc.invalidateQueries({ queryKey: portalKeys.root });
          },
        });
        return {
          reference_id: "demo",
          status: "PENDING",
          portal_publication_id: "demo",
          portal: args.portal,
        };
      }
      return invokePortalPublish({
        portal: args.portal,
        action: args.action,
        bienId,
        force: args.force,
      });
    },
    onSuccess: async () => {
      // Préfixe : la fiche (par bien) ET les colonnes de liste (par portail).
      await qc.invalidateQueries({ queryKey: portalKeys.root });
      await qc.invalidateQueries({ queryKey: ["biens"] });
    },
  });
}
